/**
 * Tier-derivation heuristic for provider-native tool keys — ENG-5127
 * (seed generation), promoted to @augmented/core in ENG-6027 so the
 * runtime Composio HITL gate and the seed/drift tooling share ONE
 * implementation.
 *
 * Maps a tool key (e.g. "GMAIL_SEND_EMAIL") to its baseline
 * `min_hitl_tier` using lexical patterns. Two consumers:
 *
 *   1. Seed generation + drift check (`packages/supabase/scripts/`) —
 *      produces the *initial* value for `tool_definitions.min_hitl_tier`;
 *      the seed file is the source of truth once written (humans can
 *      RAISE above the heuristic and it sticks).
 *   2. Runtime catalog-miss fallback (ENG-6027) — the Composio
 *      managed-toolkits lane discovers tools dynamically via MCP
 *      tools/list, so catalog coverage is structurally incomplete.
 *      When a called tool has no `tool_definitions` row, the gate
 *      applies this heuristic as the floor (audited with
 *      `catalog_miss: true`) instead of hard-blocking every
 *      uncatalogued toolkit.
 *
 * IMPORTANT: these two consumers MUST stay on this single function.
 * A forked copy means a tool can classify differently at seed time vs
 * runtime — non-deterministic enforcement. The previous copy at
 * `packages/supabase/scripts/lib/tool-tier-heuristic.ts` is now a thin
 * re-export of this module.
 *
 * The patterns are derived from the canonical Composio naming
 * conventions surveyed across `seeds/integration-definitions.json`:
 *
 *   read              → *_GET_*, *_FIND_*, *_LIST_*, *_SEARCH_*, *_FETCH_*,
 *                       *_RETRIEVE_*, *_EXPORT_*, *_DOWNLOAD_* (plus the
 *                       native kebab / bare snake_case leading forms
 *                       `get-…` / `get_…` / `list_…`)
 *   write             → *_ASSERT_*, *_UPSERT_*, *_PATCH_*, *_UPDATE_*,
 *                       *_INSERT_*, *_CREATE_*, *_ADD_*, *_MOVE_*
 *   write_high_risk   → *_SEND_*, *_POST_*, *_PUBLISH_*, *_REPLY_*,
 *                       *_FORWARD_*, *_EMAIL_*, *_INVITE_*, *_NOTIFY_*,
 *                       *_BATCH_MODIFY_*, *_IMPORT_*
 *                       (NB: these NOUNS name the object, not the action, so
 *                       a read-verb prefix demotes them to `read` — e.g.
 *                       GMAIL_FETCH_EMAILS / HUBSPOT_GET_ACTIVE_IMPORTS_LIST /
 *                       GMAIL_GET_AUTO_FORWARDING are reads, not sends,
 *                       ENG-7684. A genuine action like GMAIL_SEND_EMAIL has
 *                       no read verb and stays write_high_risk. Likewise a
 *                       draft *composition* - a compose verb plus a `_DRAFT`
 *                       object, e.g. GMAIL_CREATE_EMAIL_DRAFT - is demoted to
 *                       `write`: drafting dispatches nothing, ENG-7980. A real
 *                       dispatch like GMAIL_SEND_DRAFT has a send verb and
 *                       stays write_high_risk.)
 *   write_destructive → *_DELETE_*, *_REVOKE_*, *_REMOVE_*, *_TRASH_*,
 *                       *_DROP_*, *_BATCH_DELETE_*, *_CLEAR_*, *_HIDE_*
 *   admin             → *_PERMISSION*, *_GRANT_*, *_AUTHORIZE_*, *_OAUTH*,
 *                       *_WEBHOOK*, *_SETTINGS*, *_CONFIG*, *_VAULT*,
 *                       *_CREDENTIAL*, *_ENROL*, *_REGISTER*
 *                       (NB: the _SETTINGS and _CONFIG patterns are admin
 *                       only when mutated — a read-verb prefix like GET_ or
 *                       LIST_ makes them a benign lookup that classifies as
 *                       `read`.)
 *
 * Ordering matters: more-specific stricter patterns are tested before
 * generic write patterns so that e.g. `GMAIL_BATCH_DELETE_MESSAGES`
 * lands at `write_destructive`, not `write`. No match falls back to
 * `write` — unrecognised verb shapes lean towards "do something"
 * rather than "look at something"; humans can downgrade to `read` in
 * the seed if appropriate.
 *
 * Catalog floor; site-specific overrides live on integration_definitions
 * and agent_integrations per ENG-5126.
 */

import { HITL_TIER_RANK, type HitlTier } from '../types/integration.js';

interface PatternEntry {
  /** The compiled matcher. */
  re: RegExp;
  /**
   * Human-facing token recorded in `source_metadata.heuristic_pattern`.
   * For a read-verb-guarded high-risk pattern this is the bare noun/verb
   * core (e.g. `_EMAIL`), NOT the wrapped source — so the seed stays
   * readable and a genuine high-risk tool's metadata is unchanged when the
   * guard is added (only the tools that actually re-classify churn).
   */
  label: string;
}

interface TierPattern {
  tier: HitlTier;
  patterns: PatternEntry[];
}

/** Canonical read-VERB set (superset of the SETTINGS/CONFIG relaxation).
 * Kept as one string so the high-risk guard and the bare-snake_case read
 * pattern below stay in lockstep. */
const READ_VERBS =
  'GET|LIST|FIND|FETCH|RETRIEVE|SEARCH|QUERY|DESCRIBE|INSPECT|SCAN|SHOW|VIEW|READ|EXPORT|DOWNLOAD';

/**
 * Negative lookahead: the name does NOT carry a read-verb token as its
 * ACTION. A read verb counts when it's delimited by `_` or `-` on the right
 * and either the start-of-key or a `_`/`-` on the left — so it fires on
 * Composio infix (`…_GET_…`), a Composio/native leading verb (`GET_…`,
 * `get_…`, `get-…`), but NOT on a read-shaped NOUN suffix
 * (`…_TO_CUSTOMER_LIST`, no trailing delimiter), which keeps the ambiguous
 * `_LIST`/`_VIEW` noun from demoting a genuine mutation. Delimiter-agnostic
 * (underscore + hyphen) so no naming convention — Composio, snake_case, or
 * kebab-case — can re-introduce the ENG-7684 collision.
 */
const NO_READ_VERB_ACTION = `(?!(?:^|.*[_-])(${READ_VERBS})[_-])`;

/** Compose verbs that, paired with a `_DRAFT` object, mean the tool is
 * *composing* a draft, not dispatching it. Kept separate from the dispatch
 * verbs below so `_SEND_DRAFT` is never treated as a compose. */
const DRAFT_COMPOSE_VERBS = 'CREATE|COMPOSE|SAVE|UPDATE|ADD';

/** Dispatch verbs that make a tool a genuine send even when it also names a
 * draft object. If any is present the compose exclusion must NOT fire, so a
 * mixed name like `GMAIL_CREATE_AND_SEND_DRAFT` stays write_high_risk.
 *
 * CS-1526: `REPLY` and `FORWARD` are deliberately NOT in this list. Inside a
 * name that already carries BOTH a compose verb and a `_DRAFT` object, they
 * name the KIND of draft, not the act of sending one — `OUTLOOK_CREATE_
 * FORWARD_DRAFT` composes a forward draft and nothing leaves the mailbox.
 * Including them made the tier depend on token POSITION rather than meaning:
 * `OUTLOOK_CREATE_DRAFT_REPLY` demoted (trailing `_REPLY` has no following
 * delimiter, so the exclusion missed it) while the semantically identical
 * `OUTLOOK_CREATE_FORWARD_DRAFT` stayed gated (`_FORWARD_` is delimited on
 * both sides). Two tools that do the same thing, opposite tiers, decided by
 * word order.
 *
 * Dropping them is safe because a genuine reply/forward DISPATCH does not
 * satisfy the compose half of the guard: `OUTLOOK_REPLY_EMAIL` and
 * `OUTLOOK_FORWARD_MESSAGE` carry no `_DRAFT` object at all, and
 * `OUTLOOK_FORWARD_DRAFT` (dispatch an existing draft) carries no compose
 * verb — all three keep write_high_risk via their own patterns. Only a
 * name that composes AND names a draft is demoted. */
const DRAFT_DISPATCH_VERBS = 'SEND|POST|PUBLISH|DISPATCH|SUBMIT';

/**
 * Negative lookahead: the name is NOT a draft *composition* (ENG-7980). A
 * draft-create like `GMAIL_CREATE_EMAIL_DRAFT` carries a high-risk NOUN
 * (`_EMAIL`) but produces nothing that leaves the mailbox - drafting is
 * low-friction and must not route to human approval. It fires only when ALL of
 * a compose verb (CREATE/COMPOSE/SAVE/UPDATE/ADD, delimited), NO dispatch verb
 * (SEND/POST/PUBLISH/..., delimited), AND a `_DRAFT` object token (suffix or
 * delimited) hold - so a genuine dispatch keeps its tier: `GMAIL_SEND_DRAFT`
 * has no compose verb, `GMAIL_CREATE_AND_SEND_DRAFT` carries a dispatch verb,
 * and both stay write_high_risk via the earlier `_SEND` pattern, while a plain
 * `GMAIL_CREATE_EMAIL` (no draft) is untouched. Analogous to the ENG-7684
 * read-verb guard: a high-risk noun inside a benign action is not high-risk.
 */
const NO_DRAFT_COMPOSE_ACTION = `(?!(?=.*(?:^|[_-])(?:${DRAFT_COMPOSE_VERBS})[_-])(?!.*(?:^|[_-])(?:${DRAFT_DISPATCH_VERBS})[_-]).*_DRAFT(?:_|$))`;

/**
 * Like NO_READ_VERB_ACTION but ALSO treats a read verb at END-of-key as a
 * read (`…_SETTINGS_GET`, `…_SETTINGS_LIST`) — i.e. the trailing delimiter is
 * optional (`[_-]` OR end-of-string). Safe ONLY where the object noun is
 * unambiguous (`_SETTINGS`/`_CONFIG`): a settings tool ending in `_GET`/`_LIST`
 * is unmistakably a read, whereas a generic name ending in `_LIST`/`_VIEW`
 * could be a noun (`…_TO_CUSTOMER_LIST`). Do NOT use this for the high-risk
 * guard — that noun-suffix safety is deliberate (ENG-7684). ENG-7695.
 */
const NO_READ_VERB_INCL_SUFFIX = `(?!(?:^|.*[_-])(${READ_VERBS})(?:[_-]|$))`;

/** Plain pattern — label mirrors the regex source. */
function p(re: RegExp): PatternEntry {
  return { re, label: re.source };
}

/**
 * A write_high_risk pattern that must NOT fire on a read-verb-prefixed
 * name (ENG-7684). The `_EMAIL` / `_IMPORT` / `_FORWARD` / `_POST` /
 * `_PUBLISH` nouns describe the OBJECT, not the ACTION — so a tool that
 * *reads* that object (GMAIL_FETCH_EMAILS, HUBSPOT_GET_ACTIVE_IMPORTS_LIST,
 * GMAIL_GET_AUTO_FORWARDING, SALESFORCE_GET_..._WITH_POST) is a read and
 * must auto-execute, not route to human approval and hang. Prepending the
 * read-verb negative lookahead lets those fall through to the `read` tier,
 * exactly as the _SETTINGS/_CONFIG guard already does in the admin tier.
 *
 * `core.source` is spliced verbatim so mid-pattern lookbehinds (e.g.
 * `(?<!navigate)_FORWARD`) survive, and the label keeps the bare core so a
 * genuine high-risk tool (…_SEND_EMAIL) reports `_EMAIL` as before.
 *
 * Read-verb here means a read *verb* token, never a read-shaped *noun*: the
 * ambiguous `_LIST`/`_VIEW` suffix (…_TO_CUSTOMER_LIST) only demotes when it
 * appears as an infix action token (`_LIST_`), so a genuine send/mutation
 * whose noun ends in "LIST" is untouched.
 */
function highRisk(core: RegExp): PatternEntry {
  return {
    re: new RegExp(`^${NO_READ_VERB_ACTION}${NO_DRAFT_COMPOSE_ACTION}.*${core.source}`, 'i'),
    label: core.source,
  };
}

// Ordered from strictest to most permissive. The first match wins; this
// ensures e.g. DELETE_PERMISSION (admin) beats DELETE (destructive).
const TIER_PATTERNS: TierPattern[] = [
  {
    tier: 'admin',
    patterns: [
      p(/_PERMISSION/i),
      p(/_GRANT[_S]?(_|$)/i),
      p(/_AUTHORI[SZ]E/i),
      // Reading OAuth grants / a webhook's config can expose secrets or the
      // full authorization surface, so — unlike _SETTINGS/_CONFIG — these
      // stay admin even with a read verb (deliberate; see the
      // tool-tier-heuristic test for HUBSPOT_LIST_GRANTED_OAUTH_SCOPES /
      // ATTIO_GET_WEBHOOK).
      p(/_OAUTH/i),
      p(/_WEBHOOK/i),
      // SETTINGS / CONFIG are admin only when MUTATED. A read verb anywhere —
      // prefix (GET_..._SETTINGS), infix (..._GET_..._SETTINGS), or suffix
      // (..._SETTINGS_GET / ..._SETTINGS_LIST) — means it's a settings
      // *lookup*, so it falls through to `read` and auto-executes instead of
      // routing to approval and hanging (e.g. GOOGLEANALYTICS_GET_DATA_RETENTION_SETTINGS,
      // GOOGLECALENDAR_LIST_SETTINGS, GOOGLECALENDAR_SETTINGS_GET). The suffix
      // form is safe HERE because the object noun (SETTINGS/CONFIG) is
      // unambiguous; the generic high-risk guard stays suffix-blind on purpose
      // (a trailing _LIST/_VIEW can be a noun — ENG-7684). Unlike
      // _OAUTH/_WEBHOOK/_CREDENTIAL/_VAULT/_PERMISSION above, reading app
      // settings/config is benign, so only the mutating form stays admin.
      p(new RegExp(`^${NO_READ_VERB_INCL_SUFFIX}.*_SETTINGS?(_|$)`, 'i')),
      p(new RegExp(`^${NO_READ_VERB_INCL_SUFFIX}.*_CONFIG`, 'i')),
      p(/_VAULT/i),
      p(/_CREDENTIAL/i),
      p(/_ENROL/i),
      p(/_REGISTER/i),
    ],
  },
  {
    tier: 'write_destructive',
    patterns: [
      p(/_BATCH_DELETE/i),
      p(/_DELETE(_|$)/i),
      p(/_REVOKE/i),
      p(/_REMOVE(_|$)/i),
      p(/_TRASH/i),
      p(/_DROP(_|$)/i),
      p(/_CLEAR(_|$)/i),
      p(/_HIDE(_|$)/i),
      // Kebab-case verbs used by native MCP servers (e.g. xero's `void-invoice`).
      p(/^void-/i),
    ],
  },
  {
    // Every pattern here is read-verb-guarded (ENG-7684): a high-risk NOUN
    // (_EMAIL, _IMPORT, _FORWARD, _POST, _PUBLISH) inside a read-verb name
    // (…_GET_…, …_FETCH_…) is a read, not a high-risk action, and must
    // auto-execute instead of routing to approval.
    tier: 'write_high_risk',
    patterns: [
      highRisk(/_BATCH_MODIFY/i),
      highRisk(/_SEND(_|$)/i),
      highRisk(/_POST(_|$)/i),
      highRisk(/_PUBLISH/i),
      highRisk(/_REPLY/i),
      // Message/email forwarding is high-risk. The negative lookbehind
      // exempts browser *navigation* forward (e.g. Anchor's
      // `anchor_navigate_forward`), which is benign and classifies as read;
      // the read-verb guard additionally exempts GMAIL_GET_AUTO_FORWARDING.
      highRisk(/(?<!navigate)_FORWARD/i),
      highRisk(/_EMAIL/i),
      highRisk(/_INVITE/i),
      highRisk(/_NOTIFY/i),
      highRisk(/_IMPORT/i),
      // Sharing-preference MUTATIONS grant external access to content —
      // exfiltration-shaped, so they must not fall through to the generic
      // `_ADD`/`_UPDATE` write patterns (ENG-6027 council finding: e.g.
      // GOOGLEDRIVE_ADD_FILE_SHARING_PREFERENCE). Anchored to a mutating
      // verb so read-shaped names (METAADS_LIST_*_SHARING_REQUESTS,
      // GOOGLEANALYTICS_GET_DATA_SHARING_SETTINGS) keep their read/admin
      // classification.
      highRisk(/_(ADD|UPDATE|SET|CREATE|MODIFY|CHANGE|ENABLE)_[A-Z0-9_]*SHAR(E|ING)/i),
    ],
  },
  {
    tier: 'write',
    patterns: [
      p(/_ASSERT/i),
      p(/_UPSERT/i),
      p(/_PATCH/i),
      // The negative lookbehind prevents `MONDAY_GET_UPDATES` (and similar
      // "fetch the comment thread" reads) from being mis-tagged as a write
      // just because the noun happens to be "update".
      p(/(?<!_GET)_UPDATE/i),
      p(/_INSERT/i),
      p(/_CREATE/i),
      p(/_ADD(_|$)/i),
      p(/_MOVE(_|$)/i),
      p(/_COPY(_|$)/i),
      // Kebab-case verbs used by native MCP servers (e.g. xero).
      p(/^create-/i),
      p(/^update-/i),
      p(/^attach-/i),
    ],
  },
  {
    tier: 'read',
    patterns: [
      p(/_GET/i),
      p(/_FIND/i),
      p(/_LIST/i),
      p(/_SEARCH/i),
      p(/_FETCH/i),
      p(/_RETRIEVE/i),
      p(/_EXPORT/i),
      p(/_DOWNLOAD/i),
      p(/_READ(_|$)/i),
      p(/_VIEW(_|$)/i),
      // Cloud-style verbs (used by aws_*, gcloud, kubectl etc.) and other
      // common read-shaped names that don't follow Composio's GET prefix.
      p(/_DESCRIBE/i),
      p(/_CHECK/i),
      p(/_INSPECT/i),
      p(/_QUERY/i),
      p(/_SCAN/i),
      p(/_SHOW(_|$)/i),
      // Native MCP servers use bare leading verbs in snake_case (Kajabi:
      // `get_landing_page`, `list_people`) or kebab-case (xero: `list-invoices`,
      // `get-invoice`; github: `search-code`; granola: `read-transcript`). The
      // uppercase/infix `_GET`/`_LIST` patterns miss both, so they fell through
      // to the `write` fallback. One delimiter-agnostic pattern covers every
      // read verb in either convention. Read is the lowest-precedence tier, so
      // a leading-verb MUTATION still matches earlier: `get_or_create_x` hits
      // `write` (_CREATE), `list_credentials` hits `admin` (_CREDENTIAL).
      // ENG-7684.
      p(new RegExp(`^(${READ_VERBS})[_-]`, 'i')),
      // Reach-estimate GENERATION is a read-analytics operation, not a content
      // mutation: it starts an async unique-listener estimate (nothing is
      // created or changed) and the result is polled via a separate `get_*`.
      // The bare `generate` verb isn't in the read set, so without this these
      // fall through to the `write` fallback and read as mutations (ENG-8009).
      // Anchored to the `generate_<entity>_reach_estimate` shape so it only ever
      // matches analytics estimate triggers (Omny: generate_{org,program,clip}_
      // reach_estimate), never a genuine generator like `generate_video`. Read
      // is lowest-precedence, so a name that also matches a stricter pattern
      // (e.g. a hypothetical `delete_..._reach_estimate`) still wins there.
      p(/^generate_[a-z]+_reach_estimate$/i),
      // `report-*` (xero's `report-profit-and-loss`) — not a read verb but a
      // read-shaped native prefix. Anchored so it can't match report-* writers.
      p(/^report-/i),
      // ENG-5855: Anchor Browser observation tools (hosted MCP, `anchor_*`
      // names). Anchored to `^anchor_` so they only ever classify Anchor's
      // own tools and can't reclassify another toolkit's keys when the seed
      // is regenerated. The other read-shaped Anchor tools already match
      // generic patterns (`anchor_get_body_html` → _GET, `anchor_tab_list`
      // → _LIST). `anchor_network_requests` is deliberately NOT here — it
      // dumps auth headers/tokens, so it's raised to write_high_risk as a
      // manual escalation in the seed.
      p(/^anchor_navigate/i),
      p(/^anchor_snapshot/i),
      p(/^anchor_take_screenshot/i),
      p(/^anchor_wait_for/i),
      p(/^anchor_console_messages/i),
    ],
  },
];

/**
 * The pattern that drove the decision — recorded in `source_metadata`
 * for drift detection and human auditing, and in `guardrail_audit_log`
 * details for runtime catalog-miss decisions.
 */
export interface HeuristicMatch {
  tier: HitlTier;
  /** The first pattern (as source string) that matched. */
  matched_pattern: string;
}

/**
 * Apply the tier heuristic to a tool key. Returns the strictest tier whose
 * pattern matches, or `write` as a conservative fallback when nothing
 * matches (unrecognised verb shapes lean towards "do something" rather
 * than "look at something"; humans can downgrade to `read` in the seed
 * if appropriate).
 */
export function heuristicTier(toolKey: string): HeuristicMatch {
  for (const { tier, patterns } of TIER_PATTERNS) {
    for (const { re, label } of patterns) {
      if (re.test(toolKey)) {
        return { tier, matched_pattern: label };
      }
    }
  }
  return { tier: 'write', matched_pattern: '__fallback__' };
}

/**
 * Comparator returning whether `a` is stricter than `b` by canonical rank.
 * Useful for drift-check assertions ("seeded value must be ≥ heuristic").
 */
export function isStricterThan(a: HitlTier, b: HitlTier): boolean {
  return HITL_TIER_RANK[a] > HITL_TIER_RANK[b];
}
